import {
  Avatar,
  Box,
  Button,
  Typography,
  Tabs,
  Tab,
  Grid,
  Paper,
  Stack,
  Badge,
} from '@mui/material';
import { hasFeature } from '../../permissions';
import PermissionDialog from '../ui/Modal/PermissionDialog';
import DisplayCard from '../ui/Card/DisplayCard';
import DisplayCardShimmer from '../ui/Card/DisplayCardShimmer';
import ConfirmDialog from '../ui/Modal/ConfirmDialog';
import UndoSnackbar from '../ui/Feedback/UndoSnackbar';
import { useUndoableDelete } from '../../hooks/useUndoableDelete';
import { deleteHotelSection, undoRestaurantDelete } from '../../api/hotelinfoApi';
import { fetchRestaurantOrders } from '../../api/orderApi';
import useRestaurantNotificationStore from '../../stores/restaurantNotificationStore';
import socket from '../../socket';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:7071';

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Plus } from 'lucide-react';
import RestaurantFormModal from './RestaurantFormModal';
import { useNavigate } from 'react-router-dom';
import RestaurantOrders from '../RestaurantManager/RestaurantOrders';

const formatOpeningHours = (restaurant) => {
  const hours = restaurant?.openingHours;
  if (!Array.isArray(hours) || hours.length === 0) return 'Hours unavailable';
  const first = hours[0];
  const open = first?.open ? first.open : null;
  const close = first?.close ? first.close : null;
  if (!open || !close) return 'Hours unavailable';
  return `${open} – ${close}`;
};

export default function RestaurantSection({
  entries,
  loading,
  setEntries,
  form,
  setForm,
  open,
  setOpen,
  handleEdit,
  handleDelete,
  propertyId,
  fetchEntries,
  allowedFeatures
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deletedCardId, setDeletedCardId] = useState(null);

  const [permOpen, setPermOpen] = useState(false);
  const [permMsg, setPermMsg] = useState('');
  const showPerm = (msg) => { setPermMsg(msg); setPermOpen(true); };

  const [activeTab, setActiveTab] = useState(0);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(null);

  // State for all orders
  const [allOrders, setAllOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(null);

  // Local badge counts keyed by restaurantId (resilient to server event name changes)
  const [badgeCounts, setBadgeCounts] = useState({});
  // Derived counts from REST-loaded orders (seed on first load)
  const [derivedBadgeCounts, setDerivedBadgeCounts] = useState({});

  // Zustand store for notifications (still used for incremental bumps)
  const { newOrderCounts, incrementNewOrderCount, clearNewOrderCount } = useRestaurantNotificationStore();

  // Prefer live counts coming from socket; fall back to store if empty
  const totalNewOrderCount = useMemo(() => {
    const source = Object.keys(badgeCounts).length ? badgeCounts : newOrderCounts;
    return Object.values(source).reduce((sum, count) => sum + (Number(count) || 0), 0);
  }, [badgeCounts, newOrderCounts]);

  const getLocalAF = () => {
    try {
      return JSON.parse(localStorage.getItem('allowedFeatures') || '{}');
    } catch (e) {
      console.warn('[RestaurantSection] Failed to parse allowedFeatures from localStorage:', e);
      return {};
    }
  };

  const AF = allowedFeatures || getLocalAF();
  const canAdd = !!(AF?.restaurants?.addRestaurant ?? hasFeature('restaurants.addRestaurant'));
  const canEdit = !!(AF?.restaurants?.editRestaurant ?? hasFeature('restaurants.editRestaurant'));
  const canDelete = !!(AF?.restaurants?.deleteRestaurant ?? hasFeature('restaurants.deleteRestaurant'));

  const navigate = useNavigate();

  const { triggerDelete, triggerUndo, showUndo, remainingTime } = useUndoableDelete({
    onDeleteApi: (item) => deleteHotelSection('restaurants', item._id, propertyId),
    onUndoApi: (item) => undoRestaurantDelete(item._id, propertyId),
  });

  // Treat these statuses as "new" for badge purposes
  const NEW_STATUSES = useMemo(() => new Set([
    'awaiting',
    'awaiting_confirmation',
    'awaiting confirmation',
    'pending',
    'new',
    'submitted',
    'unconfirmed'
  ]), []);

  const pickStatus = (o) => {
    const s1 = o?.status ?? o?.currentStatus ?? o?.statusCode ?? o?.state;
    if (s1) return String(s1).toLowerCase();
    const hist = Array.isArray(o?.statusHistory) ? o.statusHistory : [];
    if (hist.length) {
      const last = hist[hist.length - 1];
      if (last?.status) return String(last.status).toLowerCase();
      if (last?.code) return String(last.code).toLowerCase();
    }
    return '';
  };

  const pickRestaurantId = (o) =>
    (o?.restaurantId && (o.restaurantId._id || o.restaurantId)) ||
    (o?.restaurant && (o.restaurant._id || o.restaurant)) ||
    null;

  const computeDerivedCounts = useCallback((orders = []) => {
    const byRestaurant = {};
    for (const o of orders) {
      const status = pickStatus(o);
      if (!status || !NEW_STATUSES.has(status)) continue;
      const rId = pickRestaurantId(o);
      if (!rId) continue;
      byRestaurant[rId] = (byRestaurant[rId] || 0) + 1;
    }
    return byRestaurant;
  }, [NEW_STATUSES]);

  // Clear the visible badge for a restaurant (local + store)
  const clearRestaurantBadge = useCallback((restaurantId) => {
    if (!restaurantId) return;
    setBadgeCounts((prev) => {
      if (!prev || typeof prev !== 'object') return prev;
      if (prev[restaurantId] === 0 || prev[restaurantId] == null) return prev;
      return { ...prev, [restaurantId]: 0 };
    });
    setDerivedBadgeCounts((prev) => {
      if (!prev || typeof prev !== 'object') return prev;
      if (prev[restaurantId] === 0 || prev[restaurantId] == null) return prev;
      return { ...prev, [restaurantId]: 0 };
    });
    try { clearNewOrderCount(restaurantId); } catch {}
  }, [clearNewOrderCount]);

  // Fetch all orders for the property
  const loadAllOrders = useCallback(async () => {
    const pid = (propertyId && (propertyId.id || propertyId)) || null;
    if (!pid) {
      console.warn('[RestaurantSection] loadAllOrders skipped: no propertyId');
      return;
    }
    setOrdersLoading(true);
    setOrdersError(null);
    try {
      const { data } = await fetchRestaurantOrders({ propertyId: pid });
      const list = Array.isArray(data) ? data : Array.isArray(data?.orders) ? data.orders : [];
      setAllOrders(list);
      // Seed badge counts from REST data and expose for quick console checks
      setDerivedBadgeCounts(computeDerivedCounts(list));
      if (typeof window !== 'undefined') {
        window.__EF_DEBUG_ORDERS__ = list;
        console.log('[RestaurantSection] loaded orders:', list.length);
      }
    } catch (err) {
      console.error('[RestaurantSection] failed to fetch all orders', err);
      setOrdersError(err?.response?.data?.message || err?.message || 'Failed to load orders');
    } finally {
      setOrdersLoading(false);
    }
  }, [propertyId, computeDerivedCounts]);

  useEffect(() => {
    loadAllOrders();
  }, [loadAllOrders]);

  useEffect(() => {
    setDerivedBadgeCounts(computeDerivedCounts(allOrders));
  }, [allOrders, computeDerivedCounts]);

  // Socket logic for real-time updates
  useEffect(() => {
    const pid = (propertyId && (propertyId.id || propertyId)) || null;
    if (!pid) {
      console.warn('[RestaurantSection] socket join skipped: no propertyId');
      return;
    }
    if (!socket || typeof socket.emit !== 'function') {
      console.error('[RestaurantSection] socket is not available or has no emit. Import path?', socket);
      return;
    }

    // Ensure connection, then join the property-wide orders room
    const doJoin = () => {
      try {
        socket.emit('joinRestaurantOrdersRoom', pid);
        console.log('[socket] joinRestaurantOrdersRoom', { propertyId: pid, socketId: socket.id, connected: socket.connected });
      } catch (e) {
        console.error('[socket] failed to emit joinRestaurantOrdersRoom', e);
      }
    };

    if (!socket.connected) {
      console.log('[socket] not connected; calling connect() …');
      try { socket.connect?.(); } catch (e) { console.error('[socket] connect() threw', e); }
      socket.once?.('connect', doJoin);
      // safety: if connect never fires (CORS?), still attempt a delayed join
      const t = setTimeout(doJoin, 1500);
      // cleanup of the timer below in return()
      var delayedJoinTimer = t;
    } else {
      doJoin();
    }

    const onConnect = () => {
      console.log('[socket] connected', { id: socket.id, transport: socket.io?.engine?.transport?.name });
    };
    const onReconnect = (attempt) => console.log('[socket] reconnect_attempt', attempt);
    const onDisconnect = (reason) => console.log('[socket] disconnect', reason);
    const onError = (err) => console.log('[socket] connect_error', err);

    socket.on('connect', onConnect);
    if (socket.io?.on) socket.io.on('reconnect_attempt', onReconnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onError);

    // Wildcard logger (if available)
    const onAny = (event, ...args) => {
      if (!String(event).startsWith('chat')) console.log('[socket][ANY]', event, args?.[0]);
    };
    if (socket.onAny) socket.onAny(onAny);

    // Normalize counts payloads coming from various server emit shapes
    const handleCounts = (payload = {}) => {
      // accepted shapes:
      // { byRestaurant: { [id]: n } } or { counts: { [id]: n } } or { pendingCountByRestaurantId: { ... } }
      const byRestaurant =
        payload.byRestaurant ||
        payload.counts ||
        payload.pendingCountByRestaurantId ||
        {};
      setBadgeCounts((prev) => ({ ...prev, ...byRestaurant }));
    };

    const handleNewOrderLegacy = (newOrder) => {
      console.log('[socket] (legacy) newOrder:', newOrder);
      setAllOrders((prev) => [newOrder, ...prev]);
      const rId = pickRestaurantId(newOrder);
      if (rId) {
        setBadgeCounts((prev) => ({ ...prev, [rId]: (Number(prev?.[rId]) || 0) + 1 }));
        incrementNewOrderCount(rId);
      }
    };

    const handleNewOrder = (evt) => {
      console.log('[socket] restaurant:order:created', evt);
      const newOrder = evt?.order || evt;
      setAllOrders((prev) => (newOrder ? [newOrder, ...prev] : prev));
      const rId = pickRestaurantId(newOrder) || evt?.restaurantId;
      if (rId) {
        setBadgeCounts((prev) => ({ ...prev, [rId]: (Number(prev?.[rId]) || 0) + 1 }));
        incrementNewOrderCount(rId);
      }
    };

    const handleStatusUpdate = (data) => {
      console.log('[socket] status update:', data);
      setAllOrders((prev) =>
        prev.map((order) =>
          order._id === (data.orderId || data._id)
            ? { ...order, status: data.status || order.status, statusHistory: data.statusHistory || order.statusHistory }
            : order
        )
      );
      // If this update is for the currently selected restaurant and it leaves the NEW bucket, reduce badge to zero.
      try {
        const rid = data?.restaurantId?._id || data?.restaurantId || data?.restaurant?._id;
        const s = (data?.status || data?.currentStatus || data?.statusCode || data?.state || '').toLowerCase();
        if (rid && s && !NEW_STATUSES.has(s) && rid === selectedRestaurantId) {
          clearRestaurantBadge(rid);
        }
      } catch {}
    };

    // Listen to multiple possible event names for resilience across server versions
    socket.on('restaurant:orders:counts', handleCounts);
    socket.on('orders:counts', handleCounts);

    socket.on('restaurant:order:created', handleNewOrder);
    socket.on('newOrder', handleNewOrderLegacy);

    socket.on('restaurant:order:status', handleStatusUpdate);
    socket.on('orderStatusUpdate', handleStatusUpdate);

    return () => {
      socket.off('restaurant:orders:counts', handleCounts);
      socket.off('orders:counts', handleCounts);

      socket.off('restaurant:order:created', handleNewOrder);
      socket.off('newOrder', handleNewOrderLegacy);

      socket.off('restaurant:order:status', handleStatusUpdate);
      socket.off('orderStatusUpdate', handleStatusUpdate);

      socket.off('connect', onConnect);
      if (socket.io?.off) socket.io.off('reconnect_attempt', onReconnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onError);
      if (socket.offAny) socket.offAny(onAny);
      try { socket.off?.('connect', doJoin); } catch {}
      if (typeof delayedJoinTimer !== 'undefined') clearTimeout(delayedJoinTimer);
    };
  }, [propertyId, incrementNewOrderCount, selectedRestaurantId, clearRestaurantBadge, NEW_STATUSES]);

  useEffect(() => {
    if (!entries?.length) {
      setSelectedRestaurantId(null);
      return;
    }
    if (!selectedRestaurantId || !entries.find((x) => x._id === selectedRestaurantId)) {
      setSelectedRestaurantId(entries[0]._id);
      // clear badge for the auto-selected restaurant to avoid stale bubbles
      clearRestaurantBadge(entries[0]._id);
    }
  }, [entries, selectedRestaurantId, clearRestaurantBadge]);

  const selectedRestaurant = useMemo(
    () => entries.find((r) => r._id === selectedRestaurantId) || null,
    [entries, selectedRestaurantId]
  );

  const ordersForSelectedRestaurant = useMemo(
    () => allOrders.filter((o) => pickRestaurantId(o) === selectedRestaurantId),
    [allOrders, selectedRestaurantId]
  );

  const handleManageOrdersClick = (entry) => {
    setActiveTab(0);
    setSelectedRestaurantId(entry?._id || null);
    if (entry?._id) clearRestaurantBadge(entry._id);
  };

  const handleUpdateOrder = (updatedOrder) => {
    if (!updatedOrder?._id) return;
    setAllOrders(prev => prev.map(o => o._id === updatedOrder._id ? updatedOrder : o));
  };

  // Subtab chip style (match Booking/Services/Settings look)
  const subTabSx = {
    textTransform: 'none',
    fontWeight: 600,
    color: '#6B7280',
    borderRadius: '999px',
    minHeight: 'auto',
    padding: '10px 20px',
    '&.Mui-selected': { backgroundColor: '#E9EAEC', color: '#111827' },
    '&:hover': { backgroundColor: '#F3F4F6' },
  };

  const OrdersTabLabel = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography sx={{ pr: 0 }}>Manage Orders</Typography>
      <Badge badgeContent={totalNewOrderCount} color="error" />
    </Box>
  );

  return (
    <>
      <Tabs
        value={activeTab}
        onChange={(_e, val) => setActiveTab(val)}
        aria-label="Restaurant management tabs"
        TabIndicatorProps={{ style: { display: 'none' } }}
        sx={{
          mt: 0,
          pt:4,
          px: 3,
          '& .MuiTabs-flexContainer': { gap: 2 },
        }}
      >
        <Tab disableRipple label={OrdersTabLabel} sx={subTabSx} />
        <Tab disableRipple label="Manage Restaurants" sx={subTabSx} />
      </Tabs>

      {activeTab === 1 && (
        <>
          <RestaurantFormModal
            open={open}
            onClose={() => setOpen(false)}
            initialData={form}
            onSubmit={async (data) => {
              if (data._id && !canEdit) { showPerm('You do not have permission to edit restaurants.'); return; }
              if (!data._id && !canAdd) { showPerm('You do not have permission to add restaurants.'); return; }

              const method = data._id ? 'PUT' : 'POST';
              const url = data._id
                ? `${API_BASE}/api/restaurants/${data._id}`
                : `${API_BASE}/api/restaurants`;

              const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
              });

              if (response.ok) {
                setOpen(false);
                setForm({});
                fetchEntries();
              } else {
                console.error('Failed to save restaurant');
              }
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'right', mt: 2, pr: 3 }}>
            <Button
              variant="contained"
              aria-disabled={!canAdd}
              onClick={() => {
                if (!canAdd) { showPerm('You do not have permission to add restaurants.'); return; }
                setForm({});
                setOpen(true);
              }}
              startIcon={<Plus size={18} />}
              sx={{
                backgroundColor: canAdd ? undefined : 'lightgray',
                color: canAdd ? undefined : 'white',
                opacity: canAdd ? 1 : 0.4,
                '&:hover': { backgroundColor: canAdd ? undefined : 'lightgray' },
                pointerEvents: 'auto',
                cursor: canAdd ? 'pointer' : 'not-allowed'
              }}
            >
              Add Restaurant
            </Button>
          </Box>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Grid container spacing={2} columns={12} justifyContent="center" maxWidth="xl">
              {loading ? (
                Array.from(new Array(6)).map((_, index) => (
                  <Grid
                    key={index}
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    xl={2}
                  >
                    <DisplayCardShimmer />
                  </Grid>
                ))
              ) : (
                entries.map((entry, index) => (
                  <Grid
                    key={entry._id || index}
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    xl={2}
                  >
                    <DisplayCard
                      entry={entry}
                      imageUrls={entry.photos?.length ? entry.photos : entry.imageUrls || []}
                      isDeleted={entry._id === deletedCardId}
                      onEdit={() => {
                        if (!canEdit) {
                          showPerm('You do not have permission to edit restaurants.');
                          return;
                        }
                        handleEdit(entry);
                      }}
                      onDelete={() => {
                        if (!canDelete) {
                          showPerm('You do not have permission to delete restaurants.');
                          return;
                        }
                        setDeletingId(entry._id);
                        setConfirmOpen(true);
                      }}
                      onDuplicate={async () => {
                        if (!canAdd) {
                          showPerm('You do not have permission to add restaurants.');
                          return;
                        }
                        const duplicateEntry = { ...entry, _id: undefined, title: `${entry.title} (Copy)` };
                        const method = 'POST';
                        const url = `${API_BASE}/api/restaurants`;
                        await fetch(url, {
                          method,
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(duplicateEntry),
                        });
                        fetchEntries();
                      }}
                      editAllowed={canEdit}
                      deleteAllowed={canDelete}
                      duplicateAllowed={canAdd}
                      onClick={() => navigate(`/restaurant/${entry._id}/manage`)}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {entry.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          mb: 0.5,
                        }}
                      >
                        {entry.subtitle}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {entry.address}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'green', mb: 0.5 }}>
                        {entry.bookingContact}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {entry.about}
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleManageOrdersClick(entry);
                          }}
                        >
                          Manage Orders
                        </Button>
                      </Box>
                    </DisplayCard>
                  </Grid>
                ))
              )}
            </Grid>
          </Box>

          <PermissionDialog open={permOpen} onClose={() => setPermOpen(false)} message={permMsg} />

          <ConfirmDialog
            open={confirmOpen}
            title="Delete Restaurant"
            description="Are you sure you want to delete this restaurant?"
            onCancel={() => { setConfirmOpen(false); setDeletingId(null); }}
            onConfirm={async () => {
              if (!canDelete) { console.warn('[RestaurantSection] Confirm delete blocked by permission'); return; }
              const itemToDelete = entries.find(e => e._id === deletingId);
              setDeletedCardId(deletingId);
              await triggerDelete(itemToDelete);
              setConfirmOpen(false);
              setDeletingId(null);
              fetchEntries();
            }}
          />
          <UndoSnackbar show={showUndo} label="Restaurant deleted" onUndo={() => { setDeletedCardId(null); triggerUndo(); fetchEntries(); }} remainingTime={remainingTime} />
        </>
      )}

      {activeTab === 0 && (
        <Box sx={{ mt: 3, px: 3 }}>
          {entries.length === 0 ? (
            <Typography color="text.secondary">No restaurants available yet.</Typography>
          ) : (
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Stack
                spacing={1.5}
                sx={{ width: 320, minWidth: 320, maxWidth: 320, maxHeight: 560, overflowY: 'auto', flexShrink: 0, pr: 0.5 }}
              >
                {entries.map((entry) => {
                  const isActive = entry._id === selectedRestaurantId;
                  const openingLabel = formatOpeningHours(entry);
                  const cover = entry.photos?.[0] || entry.imageUrls?.[0];
                  const notificationCount =
                    (badgeCounts?.[entry._id] ??
                     derivedBadgeCounts?.[entry._id] ??
                     newOrderCounts[entry._id] ??
                     0);
                  return (
                    <Paper
                      key={entry._id}
                      elevation={isActive ? 4 : 1}
                      onClick={() => {
                        setSelectedRestaurantId(entry._id);
                        // viewing this restaurant -> clear its badge immediately
                        clearRestaurantBadge(entry._id);
                      }}
                      sx={{
                        borderRadius: 2,
                        cursor: 'pointer',
                        border: isActive ? '2px solid #C8A86B' : '1px solid #E5E7EB',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        backgroundColor: isActive ? 'rgba(200,168,107,0.08)' : 'white',
                      }}
                    >
                      <Badge badgeContent={notificationCount} color="error">
                        <Avatar
                          src={cover}
                          alt={entry.name}
                          variant="rounded"
                          sx={{ width: 48, height: 48, borderRadius: 1.5 }}
                        >
                          {entry.name?.[0]?.toUpperCase() || 'R'}
                        </Avatar>
                      </Badge>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={600} noWrap>{entry.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{openingLabel}</Typography>
                        {entry.subtitle ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{entry.subtitle}</Typography> : null}
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>

              <Box sx={{ flex: 1 }}>
                {selectedRestaurant ? (
                  <RestaurantOrders
                    restaurantId={selectedRestaurantId}
                    propertyId={propertyId}
                    orders={ordersForSelectedRestaurant}
                    loading={ordersLoading}
                    error={ordersError}
                    onRefresh={loadAllOrders}
                    onUpdateOrder={handleUpdateOrder}
                  />
                ) : (
                  <Typography color="text.secondary" sx={{ mt: 2 }}>
                    Select a restaurant to view and manage its orders.
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </>
  );
}